import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { orbit, zoomBy, panBy, fitCamera, unprojectToPlane, SCENE_VIEW } from '../engine/battlefield/projection';
import { stepCamera, zoomAbout } from '../engine/display/cameraEase';
import { pointerToScene } from '../utils/scenePointer';
import { makeCameraStore } from './cameraStore';

/**
 * Which camera drag a press starts, or null when the press is not ours.
 *
 * THREE BUTTONS, THREE JOBS (v7.24.0). The left button belongs to the
 * work - measuring, painting, moving a marker - and the camera takes the
 * other two: RIGHT ORBITS, MIDDLE PANS. Shift-left pans as well, for the
 * laptop with no middle button.
 *
 * That is the whole reason measuring needed no mode. While orbit sat on
 * the left button every tool had to compete with it, and the fix attempted
 * first - measuring by clicking - solved that by taking away the gesture
 * that felt right. Moving orbit was always the better answer.
 */
function dragMode(e) {
  if (e.button === 1 || (e.button === 0 && e.shiftKey)) return 'pan';
  return e.button === 2 ? 'orbit' : null;
}

/**
 * The camera after a drag step of (dx, dy) scene units.
 *
 * Dragging left spins the scene left, which is how every orbit control
 * the user has ever met behaves. A pan asks where two pixels land on the
 * GROUND and moves the target by the difference, so the map sticks to the
 * pointer at every angle instead of sliding faster the flatter the view
 * gets.
 */
function draggedCamera(c, mode, dx, dy) {
  if (mode === 'orbit') return orbit(c, dx * 0.35, dy * 0.25);
  const from = unprojectToPlane(SCENE_VIEW.width / 2, SCENE_VIEW.height / 2, 0, c, SCENE_VIEW);
  const to = unprojectToPlane(SCENE_VIEW.width / 2 - dx, SCENE_VIEW.height / 2 - dy, 0, c, SCENE_VIEW);
  if (!from || !to) return c;
  return panBy(c, to.x - from.x, to.z - from.z);
}

/**
 * useBattlefieldCamera - orbit, zoom, pan and reset for the tactical map.
 *
 * POINTER CAPTURE, not a mousemove flag. Every other drag in this app
 * (the panel dividers) uses a `useRef` boolean plus a container-level
 * mousemove, which silently cancels the moment the cursor leaves the
 * container. That is tolerable for a divider and wrong for a map: the
 * natural way to spin a scene is a long sweep that runs off the edge.
 * `setPointerCapture` keeps the events coming until the button is
 * released, wherever the pointer goes.
 *
 * The camera is replaced, never mutated - the scene is memoised on its
 * identity.
 *
 * EASED on the display (plan design-gaps, T5; `eased`, off on a screen that
 * holds still): a wheel notch zooms toward the cursor, a preset or a reset
 * glides, and an arrow key (`onArrow`) turns or tilts - each setting a goal
 * the camera closes on frame by frame. A drag moves it at once, and drops
 * any glide. The classic map keeps its snap, and its wheel about the middle.
 */
export default function useBattlefieldCamera(boundary, { eased = false, orbitSlop = 0 } = {}) {
  // The camera lives in a store (hooks/cameraStore), not in this component's state: whoever draws with it subscribes
  // (`useCamera(cameraStore)`), a gesture asks `getCamera()` when it happens, and the panel holding this hook never
  // re-renders for a camera frame (perf overhaul 2b, 8f).
  const [store] = useState(() => makeCameraStore(fitCamera(boundary, SCENE_VIEW)));
  const setCamera = store.set;
  const toward = useGoal(store.get, setCamera, eased);
  useFraming(boundary, setCamera, toward);
  const { drag, onPointerDown, onPointerMove, onPointerUp, cancelDrag } = useCameraDrag(
    setCamera,
    toward.drop,
    orbitSlop,
  );
  return {
    cameraStore: store,
    getCamera: store.get,
    setCamera,
    ...useHandMoves(boundary, toward, eased),
    onPointerDown,
    onPointerMove,
    onPointerUp,
    cancelDrag,
    dropGlide: toward.drop,
    isDragging: drag,
  };
}

/** The hand's moves that go TOWARD a goal: reset, a preset, a wheel notch, an arrow key. */
function useHandMoves(boundary, toward, eased) {
  const reset = useCallback(() => toward(() => fitCamera(boundary, SCENE_VIEW)), [boundary, toward]);
  /**
   * Swing to a preset angle AND re-frame the map at it. A zoom and a
   * centre are only ever right for the angle they were chosen at: carrying
   * the old framing over pushed the whole scene off the side of the canvas
   * - which is precisely why markers could not be grabbed in the Tactical
   * view (v7.18.0). Asking for a view means asking to SEE it.
   */
  const applyPreset = useCallback(
    (preset) => toward(() => fitCamera(boundary, SCENE_VIEW, { yaw: preset.yaw, pitch: preset.pitch })),
    [boundary, toward],
  );
  const onWheel = useCallback(
    (e) => {
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      const at = eased ? scenePointOf(e) : null;
      // The ground under the cursor AS DRAWN, mid-glide too, not under the goal's not-yet-reached camera.
      const ground = at && unprojectToPlane(at.x, at.y, 0, toward.shown(), SCENE_VIEW);
      toward((c) => (at ? zoomAbout(c, factor, at, ground) : zoomBy(c, factor)));
    },
    [toward, eased],
  );
  const onArrow = useCallback((key) => ARROWS[key] && toward((c) => orbit(c, ...ARROWS[key])), [toward]);
  return { reset, applyPreset, onWheel, onArrow };
}

/** What an arrow key does to the camera (T5): left and right turn it 15 degrees, up and down tilt it 8. */
const ARROWS = { ArrowLeft: [-15, 0], ArrowRight: [15, 0], ArrowUp: [0, 8], ArrowDown: [0, -8] };

/** Where an event is on the scene, in its units (the map's one conversion), or null where the map has no size. */
function scenePointOf(e) {
  const at = pointerToScene(e);
  return Number.isFinite(at.x) && Number.isFinite(at.y) ? at : null;
}

/**
 * The camera's GOAL (T5): `toward(fn)` moves it there - built on the goal already set, so wheel notches add up -
 * gliding a share a frame when `eased`, at once otherwise. `toward.drop()` abandons a glide: a drag took over, or the
 * follow or a GM's cut did (useDisplayCamera's moves call it - a glide left running would fight them); `toward.shown()`
 * is the camera on the screen now. The glide keeps the lens the screen wears (a preset's frame has none).
 */
function useGoal(shown, setCamera, eased) {
  const goal = useRef(null);
  const frame = useRef(0);
  const glide = useGlide({ goal, frame }, setCamera);
  useEffect(() => () => window.cancelAnimationFrame(frame.current), []);
  return useMemo(() => {
    const toward = (fn) => {
      if (!eased) return setCamera((c) => fn(c));
      goal.current = withLens(fn(goal.current ?? shown()), shown());
      if (!frame.current) frame.current = window.requestAnimationFrame(glide);
    };
    toward.drop = () => {
      goal.current = null;
    };
    // A glide under way closes on what `fn` makes of its goal instead (a new arena framed at the goal's angles).
    toward.retarget = (fn) => {
      if (goal.current) goal.current = withLens(fn(goal.current), shown());
    };
    toward.shown = shown;
    return toward;
  }, [eased, setCamera, glide, shown]);
}

/** One frame of the glide, and the next asked for while a goal stands. */
function useGlide({ goal, frame }, setCamera) {
  const glide = useCallback(() => {
    frame.current = 0;
    if (!goal.current) return;
    setCamera((c) => {
      const g = goal.current;
      if (!g) return c;
      const next = stepCamera(c, g);
      if (next === g) goal.current = null;
      return next;
    });
    frame.current = window.requestAnimationFrame(glide);
  }, [goal, frame, setCamera]);
  return glide;
}

/** A goal wearing the lens the screen wears: a preset's fresh frame has none, and dropping it would flicker. */
const withLens = (goal, shown) => (shown.fov && goal.fov !== shown.fov ? { ...goal, fov: shown.fov } : goal);

/**
 * The camera framed to the arena again whenever the arena changes - and a glide under way retargeted, or it would
 * close on the OLD arena's frame (entering Build glides, and a map loaded within that second was undone, 2026-10-22).
 */
function useFraming(boundary, setCamera, toward) {
  const fittedTo = useRef(boundary);
  // A NEW ARENA deserves a new frame. Without this, loading a map leaves
  // the camera fitted to whatever was on the table before it - and Reset
  // View then jumps somewhere the map has never been. Keyed on the
  // boundary's IDENTITY, not on the terrain: moving a crate in Build mode
  // must not yank the camera out from under you. The angles you are
  // looking from are kept and only WHAT is framed changes: loading a map
  // while building top-down must not spin the camera to three-quarter and
  // stop the painting being WYSIWYG.
  useEffect(() => {
    if (fittedTo.current === boundary) return;
    fittedTo.current = boundary;
    const reframe = (c) => fitCamera(boundary, SCENE_VIEW, { yaw: c.yaw, pitch: c.pitch });
    setCamera(reframe);
    toward.retarget(reframe);
  }, [boundary, setCamera, toward]);
}

/**
 * Orbit and pan by dragging. `onPointerDown` returns false when the press
 * was not the camera's, so the panel can fall through to whatever the
 * left button means where it was pressed.
 *
 * `cancelDrag` abandons the drag in progress, leaving the camera where it
 * is. The ping wheel needs this. Right-button-down starts an orbit as it
 * always has - that is what keeps orbit instant - but if the button is
 * still down and still still after the hold delay, the gesture was a ping
 * after all, and the movement that follows is choosing a wedge. Without
 * this the map would spin while the wheel was being used.
 */
function useCameraDrag(setCamera, dropGlide, orbitSlop) {
  const drag = useRef(null);
  const onPointerDown = useCallback(
    (e) => {
      const mode = dragMode(e);
      if (!mode) return false;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, mode, from: { x: e.clientX, y: e.clientY } };
      // The hand moves the camera itself now: a glide still running would fight it.
      dropGlide();
      return true;
    },
    [dropGlide],
  );
  const onPointerMove = useDragMove(drag, setCamera, orbitSlop);
  const onPointerUp = useCallback((e) => {
    if (drag.current?.id !== e.pointerId) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    drag.current = null;
  }, []);
  const cancelDrag = useCallback(() => {
    drag.current = null;
  }, []);
  return { drag, onPointerDown, onPointerMove, onPointerUp, cancelDrag };
}

/** One drag step: the pointer's movement, in scene units, applied to the camera. */
function useDragMove(drag, setCamera, orbitSlop) {
  return useCallback(
    (e) => {
      const d = drag.current;
      if (!d || d.id !== e.pointerId) return;
      // On the display an orbit waits out the ping's slop (2026-10-21): a right press that stays put is a ping, and
      // must not nudge the view - a nudge is a hand on the camera, and took the follow away. Held in place, not
      // stored up, so it does not jump on leaving it. The classic map's orbit stays instant (usePingGesture).
      if (d.mode === 'orbit' && d.from && Math.hypot(e.clientX - d.from.x, e.clientY - d.from.y) <= orbitSlop) {
        d.x = e.clientX;
        d.y = e.clientY;
        return;
      }
      if (d.from) d.from = null;
      // Pixels the pointer moved, converted to the scene's own coordinate
      // space - the SVG scales its fixed viewBox to whatever the panel is.
      const scale = SCENE_VIEW.width / (e.currentTarget?.clientWidth || SCENE_VIEW.width);
      const dx = (e.clientX - d.x) * scale;
      const dy = (e.clientY - d.y) * scale;
      d.x = e.clientX;
      d.y = e.clientY;
      setCamera((c) => draggedCamera(c, d.mode, dx, dy));
    },
    [drag, setCamera, orbitSlop],
  );
}
